import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type { ContentSource } from 'js/src/components/animated-explainer-videos/types';

export type { ContentSource };

/**
 * Cross-feature state for the "how does the user supply their video content"
 * widget — a tab strip with 4 sources: Describe, Paste, URL, Upload.
 *
 * `script` is the source of truth for the final narration in both Describe
 * mode (LLM-generated) and Paste mode (user-typed). `describeText` is the
 * Describe-mode prompt. `importedText` is the extracted body for URL /
 * Upload modes.
 *
 * `articleId` / `documentId` are backend record ids the URL- and Upload-tab
 * import endpoints require. They are set by the consuming feature on session
 * load, off the Video record.
 */
export interface VideoContentInputState {
    contentSource: ContentSource;
    describeText: string;
    script: string;
    importedUrl: string;
    importedText: string;
    importedFileName: string;
    isImportingContent: boolean;
    articleId: number | null;
    documentId: number | null;
}

const initialState: VideoContentInputState = {
    contentSource: 'describe',
    describeText: '',
    script: '',
    importedUrl: '',
    importedText: '',
    importedFileName: '',
    isImportingContent: false,
    articleId: null,
    documentId: null,
};

const videoContentInputSlice = createSlice({
    name: 'videoContentInput',
    initialState,
    reducers: {
        setContentSource(state, action: PayloadAction<ContentSource>) {
            // A tab switch invalidates the current script and any in-progress
            // imported content. The script field is shared between modes but
            // its meaning differs (paste: user textarea; describe/url/upload:
            // AI-generated), so carrying it across would render the wrong view.
            if (state.contentSource !== action.payload) {
                state.script = '';
                state.importedText = '';
                state.importedUrl = '';
                state.importedFileName = '';
                state.isImportingContent = false;
            }
            state.contentSource = action.payload;
        },
        setDescribeText(state, action: PayloadAction<string>) {
            state.describeText = action.payload;
        },
        setScript(state, action: PayloadAction<string>) {
            state.script = action.payload;
        },
        setImportedUrl(state, action: PayloadAction<string>) {
            state.importedUrl = action.payload;
        },
        setImportedText(state, action: PayloadAction<string>) {
            state.importedText = action.payload;
        },
        setImportedFileName(state, action: PayloadAction<string>) {
            state.importedFileName = action.payload;
        },
        setIsImportingContent(state, action: PayloadAction<boolean>) {
            state.isImportingContent = action.payload;
        },
        setArticleId(state, action: PayloadAction<number | null>) {
            state.articleId = action.payload;
        },
        setDocumentId(state, action: PayloadAction<number | null>) {
            state.documentId = action.payload;
        },
        hydrateVideoContentInput(
            state,
            action: PayloadAction<{ contentSource: ContentSource; script?: string }>,
        ) {
            // Reset transient describe/import state and seed contentSource +
            // script from saved data. Preserves articleId/documentId since
            // those are session-scoped infrastructure ids, not content.
            const { articleId, documentId } = state;
            return {
                ...initialState,
                articleId,
                documentId,
                contentSource: action.payload.contentSource,
                script: action.payload.script ?? '',
            };
        },
    },
    selectors: {
        getActiveDoctext: (state) => {
            switch (state.contentSource) {
                case 'describe':
                    return state.describeText;
                case 'paste':
                    return state.script;
                case 'url':
                case 'upload':
                    return state.importedText;
            }
        },
    },
});

export const { getActiveDoctext } = videoContentInputSlice.getSelectors(
    (state: { videoContentInput: VideoContentInputState }) => state.videoContentInput,
);

export const {
    setContentSource,
    setDescribeText,
    setScript,
    setImportedUrl,
    setImportedText,
    setImportedFileName,
    setIsImportingContent,
    setArticleId,
    setDocumentId,
    hydrateVideoContentInput,
} = videoContentInputSlice.actions;

export default videoContentInputSlice;
